import type { ReactNode } from 'react';

import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import { Autocomplete, Chip, ListSubheader, TextField, Tooltip, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';

import { FieldRow } from '@/features/store-settings';

import type { ChatbotFaqIntent, ChatbotIntentDescriptor } from '../../model/types';

export interface PolicyTopicsFieldProps {
  label: ReactNode;
  description?: ReactNode;
  ariaLabel?: string;
  value: string[];
  onChange: (value: string[]) => void;
  disabled?: boolean;
  intents: ChatbotIntentDescriptor[] | undefined;
  faqIntents: ChatbotFaqIntent[] | undefined;
  faqLoading: boolean;
  faqError: boolean;
}

interface TopicOption {
  code: string;
  group: 'builtIn' | 'faq';
  secondary?: string;
}

/**
 * Dictionary-fed "Always escalate for" editor (design §17.3.2) — replaces the static
 * `ESCALATION_BUILTIN_INTENTS` free-text `ChipListField`. Options are the eligible built-in intents
 * plus the tenant's own FAQ intent codes; a stored code outside both lists (or one that always hands
 * off anyway) renders a distinct, informative chip instead of failing silently.
 */
export function PolicyTopicsField({
  label,
  description,
  ariaLabel,
  value,
  onChange,
  disabled = false,
  intents,
  faqIntents,
  faqLoading,
  faqError,
}: PolicyTopicsFieldProps) {
  const { t } = useTranslation('chatbot');

  const eligible = (intents ?? []).filter((i) => i.policyTopicEligible);
  const handOffIntents = (intents ?? []).filter((i) => i.defaultHandling === 'HAND_OFF');
  const faqCodes = (faqIntents ?? []).map((f) => f.intentCode);

  const options: TopicOption[] = [
    ...eligible.map((i) => ({ code: i.code, group: 'builtIn' as const })),
    ...faqCodes.map((code) => ({
      code,
      group: 'faq' as const,
      secondary: faqIntents?.find((f) => f.intentCode === code)?.utterances[0],
    })),
    ...(faqLoading ? [{ code: '__faq_loading__', group: 'faq' as const }] : []),
  ];

  function chipKind(code: string): {
    color: 'default' | 'info' | 'warning';
    tooltip?: string;
    icon?: boolean;
  } {
    const isEligible = eligible.some((i) => i.code === code);
    const isFaq = faqCodes.includes(code);
    if (isEligible || isFaq) {
      return isFaq
        ? {
            color: 'default',
            tooltip: faqIntents?.find((f) => f.intentCode === code)?.utterances[0],
          }
        : { color: 'default' };
    }
    const isHandOff = handOffIntents.some((i) => i.code === code);
    if (isHandOff) {
      return { color: 'info', tooltip: t('escalation.policyTopics.handedOffTooltip') };
    }
    const isKnownButNotTopic = (intents ?? []).some((i) => i.code === code);
    if (isKnownButNotTopic) {
      return { color: 'warning', tooltip: t('escalation.policyTopics.notATopicTooltip') };
    }
    if (faqLoading || faqError) {
      // Never flagged unknown while FAQ data is incomplete (design §17.3.2).
      return { color: 'default' };
    }
    return {
      color: 'warning',
      tooltip: t('escalation.policyTopics.unknownTopicTooltip'),
      icon: true,
    };
  }

  return (
    <FieldRow label={label} description={description} alignTop>
      <Autocomplete
        multiple
        disableCloseOnSelect
        disabled={disabled}
        options={options.filter((o) => o.code !== '__faq_loading__')}
        groupBy={(option) => option.group}
        getOptionLabel={(option) =>
          option.group === 'builtIn'
            ? t(`escalation.intents.${option.code}`, option.code)
            : option.code
        }
        value={value.map((code) => ({
          code,
          group: eligible.some((i) => i.code === code) ? ('builtIn' as const) : ('faq' as const),
        }))}
        isOptionEqualToValue={(option, val) => option.code === val.code}
        onChange={(_event, next) => onChange(next.map((o) => o.code))}
        renderGroup={(params) => (
          <li key={params.key}>
            <ListSubheader component="div">
              {params.group === 'builtIn'
                ? t('escalation.policyTopics.groupBuiltIn')
                : t('escalation.policyTopics.groupFaq')}
            </ListSubheader>
            <ul style={{ padding: 0 }}>{params.children}</ul>
          </li>
        )}
        renderTags={(tags, getTagProps) =>
          tags.map((tag, index) => {
            const { key, ...tagProps } = getTagProps({ index });
            const kind = chipKind(tag.code);
            const isBuiltIn =
              eligible.some((i) => i.code === tag.code) ||
              handOffIntents.some((i) => i.code === tag.code) ||
              (intents ?? []).some((i) => i.code === tag.code);
            const isUnknown = kind.color === 'warning' && kind.icon;
            const chipLabel = isUnknown
              ? t('escalation.policyTopics.unknownTopic', { code: tag.code })
              : isBuiltIn
                ? t(`escalation.intents.${tag.code}`, tag.code)
                : tag.code;
            const chip = (
              <Chip
                size="small"
                variant={kind.color === 'default' ? 'filled' : 'outlined'}
                color={kind.color}
                icon={kind.icon ? <WarningAmberRoundedIcon fontSize="small" /> : undefined}
                label={
                  <span>
                    {chipLabel}
                    {isBuiltIn && kind.color === 'default' ? (
                      <Typography
                        component="span"
                        variant="caption"
                        sx={{ ml: 0.5, fontFamily: 'monospace', opacity: 0.7 }}
                      >
                        {tag.code}
                      </Typography>
                    ) : null}
                  </span>
                }
                key={key}
                {...tagProps}
              />
            );
            return kind.tooltip ? (
              <Tooltip title={kind.tooltip} key={key}>
                {chip}
              </Tooltip>
            ) : (
              chip
            );
          })
        }
        renderOption={(props, option) => {
          const { key, ...rest } = props;
          if (option.code === '__faq_loading__') {
            return (
              <li key={key} {...rest} aria-disabled>
                {t('escalation.policyTopics.faqLoading')}
              </li>
            );
          }
          return (
            <li key={key} {...rest}>
              <span>
                {option.group === 'builtIn'
                  ? t(`escalation.intents.${option.code}`, option.code)
                  : option.code}
                {' — '}
                <Typography
                  component="span"
                  variant="caption"
                  sx={{ fontFamily: 'monospace', color: 'text.secondary' }}
                >
                  {option.code}
                </Typography>
              </span>
            </li>
          );
        }}
        renderInput={(params) => <TextField {...params} size="small" aria-label={ariaLabel} />}
      />
      {handOffIntents.length > 0 ? (
        <Typography variant="caption" color="text.secondary" component="p" sx={{ mt: 0.5 }}>
          {t('escalation.policyTopics.alwaysHandedOff', {
            intents: handOffIntents
              .map((i) => t(`escalation.intents.${i.code}`, i.code))
              .join(', '),
          })}
        </Typography>
      ) : null}
      {faqError ? (
        <Typography variant="caption" color="text.secondary" component="p">
          {t('escalation.policyTopics.faqLoadError')}
        </Typography>
      ) : null}
    </FieldRow>
  );
}
